import { FC, useState, useEffect, useMemo } from 'react';
import { Modal, ModalProps } from '../../../ui';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { QuestionnaireModel } from '../../network/questionnaires';
import { ButtonContainer } from './styles';

interface AssignQuestionnaireModalProps extends ModalProps {
  open: boolean;
  onClose?: (reason?: 'cancel' | 'success') => void;
  questionnaires: QuestionnaireModel[];
  onAssignQuestionnaire: (questionnaireId: number, dueDate: string) => void;
  isAssigningQuestionnaire: boolean | null;
}

export const AssignQuestionnaireModal: FC<AssignQuestionnaireModalProps> = ({
  open,
  onClose,
  questionnaires,
  onAssignQuestionnaire,
  isAssigningQuestionnaire,
}) => {
  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  const [dueDate, setDueDate] = useState<Date | null>(tomorrow);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<
    number | ''
  >('');

  const handleAssignQuestionnaire = () => {
    if (selectedQuestionnaire && dueDate) {
      onAssignQuestionnaire(selectedQuestionnaire, dueDate.toISOString());
    }
  };

  // Reset form on open/close
  useEffect(() => {
    if (open) {
      setSelectedQuestionnaire('');
      setDueDate(tomorrow);
    }
  }, [open, tomorrow, setSelectedQuestionnaire, setDueDate]);

  return (
    <Modal open={open} onClose={onClose} title='Assign Questionnaire'>
      <FormControl fullWidth>
        <InputLabel
          sx={{
            '&:not(.MuiInputLabel-shrink)': {
              marginTop: '0.75rem', // Apply margin only when label is not shrunk
            },
          }}
        >
          Questionnaire Type
        </InputLabel>
        <Select
          style={{ height: '3rem', marginBottom: '1rem' }}
          variant='standard'
          value={selectedQuestionnaire}
          onChange={(e) => setSelectedQuestionnaire(e.target.value as number)}
        >
          {questionnaires.map((questionnaire) => (
            <MenuItem
              key={questionnaire.id}
              value={questionnaire.questionnaire}
            >
              {questionnaire.name}
            </MenuItem>
          ))}
        </Select>
        <DatePicker
          label='Due Date'
          value={dueDate}
          onChange={(date: Date | null) => setDueDate(date)}
          renderInput={(params) => <TextField variant='standard' {...params} />}
          minDate={tomorrow}
          shouldDisableDate={(date) => {
            // Disable today and all dates in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset hours to compare dates only
            return date <= today;
          }}
        />
        <ButtonContainer>
          <Button
            variant='contained'
            color='primary'
            onClick={handleAssignQuestionnaire}
            disabled={
              isAssigningQuestionnaire || !selectedQuestionnaire || !dueDate
            }
          >
            {isAssigningQuestionnaire ? 'Assigning...' : 'Assign'}
          </Button>
        </ButtonContainer>
      </FormControl>
    </Modal>
  );
};
